import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Assignment as TaskIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  AccessTime as TimeIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { projectsAPI, tasksAPI, timeEntriesAPI, usersAPI, customersAPI } from '../services/api';
import TaskEditDialog from '../components/TaskEditDialog';
import TaskCreateDialog from '../components/TaskCreateDialog';
import { localISODate } from '../utils/dates';
import { useAuth } from '../contexts/AuthContext';

const AllTasksPage = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]); // Store all projects for filtering
  const [tasks, setTasks] = useState([]);
  const [, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filter states
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [editingTask, setEditingTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Time entry modal
  const [showTimeEntryModal, setShowTimeEntryModal] = useState(false);
  const [, setSelectedTask] = useState(null);
  const [timeEntryData, setTimeEntryData] = useState({
    subtask_comment: '',
    hours: '',
    date: localISODate(),
  });

  useEffect(() => {
    fetchData();
  }, [showInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter projects when customer filter changes
  useEffect(() => {
    if (filterCustomer) {
      const filtered = allProjects.filter(p => p.customer_id === parseInt(filterCustomer));
      setProjects(filtered);
      // Reset project filter if current project doesn't belong to selected customer
      const currentProject = allProjects.find(p => p.id === parseInt(filterProject));
      if (!currentProject || currentProject.customer_id !== parseInt(filterCustomer)) {
        setFilterProject('');
      }
    } else {
      setProjects(allProjects);
    }
  }, [filterCustomer, allProjects, filterProject]);

  const fetchData = async () => {
    setLoading(true);
    setError('');

    try {
      const params = showInactive ? { include_inactive: true } : {};
      const [customersRes, projectsRes, tasksRes, usersRes] = await Promise.all([
        customersAPI.getAll(),
        projectsAPI.getAll(),
        tasksAPI.getAll(params),
        usersAPI.getAll(),
      ]);
      setCustomers(customersRes.data);
      setAllProjects(projectsRes.data);
      setProjects(projectsRes.data);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setIsCreating(false);
    setShowModal(true);
  };

  const handleTaskUpdated = () => {
    fetchData();
    setSuccess('Task erfolgreich aktualisiert');
  };

  const handleTaskCreated = () => {
    fetchData();
    setSuccess('Task erfolgreich erstellt');
  };

  const handleReactivateTask = async (taskId) => {
    if (!window.confirm('Möchten Sie diesen Task wirklich reaktivieren?')) {
      return;
    }
    try {
      await tasksAPI.reactivate(taskId);
      fetchData();
      setSuccess('Task erfolgreich reaktiviert');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Reaktivieren des Tasks');
      console.error(err);
    }
  };

  const openTimeEntryModal = (task) => {
    setSelectedTask(task);
    setTimeEntryData({
      project_id: task.project_id,
      task_id: task.id,
      subtask_comment: '',
      hours: '',
      date: localISODate(),
    });
    setShowTimeEntryModal(true);
  };

  const closeTimeEntryModal = () => {
    setShowTimeEntryModal(false);
    setSelectedTask(null);
    setTimeEntryData({
      project_id: '',
      task_id: '',
      subtask_comment: '',
      hours: '',
      date: localISODate(),
    });
  };

  const handleTimeEntryChange = (e) => {
    setTimeEntryData({
      ...timeEntryData,
      [e.target.name]: e.target.value,
    });
  };

  const handleTimeEntrySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await timeEntriesAPI.create({
        task_id: timeEntryData.task_id,
        subtask_comment: timeEntryData.subtask_comment,
        hours: parseFloat(timeEntryData.hours),
        date: timeEntryData.date,
        user_id: user.id,
      });
      setSuccess('Zeiteintrag erfolgreich erstellt');
      closeTimeEntryModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen des Zeiteintrags');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTask(null);
    setIsCreating(false);
  };

  const getFilteredTasks = () => {
    let filtered = [...tasks];

    // Apply project filter
    if (filterProject) {
      filtered = filtered.filter(task => task.project_id === parseInt(filterProject));
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(task =>
        task.name.toLowerCase().includes(query) ||
        (task.description && task.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  };

  const groupTasksByProject = () => {
    const grouped = {};
    const filteredTasks = getFilteredTasks();

    // Only show projects that have tasks (after filtering)
    const projectsWithTasks = filterProject
      ? projects.filter(p => p.id === parseInt(filterProject))
      : projects;

    projectsWithTasks.forEach(project => {
      const projectTasks = filteredTasks.filter(task => task.project_id === project.id);
      if (projectTasks.length > 0 || !searchQuery.trim()) {
        grouped[project.id] = {
          project,
          tasks: projectTasks
        };
      }
    });

    return grouped;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  const groupedTasks = groupTasksByProject();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={700}>
          Alle Tasks
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ borderRadius: 3, mb: 3, bgcolor: '#f8f9fa' }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FilterIcon sx={{ color: '#1e3a5f' }} />
            <TextField
              select
              label="Kunde filtern"
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
              sx={{
                minWidth: 250,
                bgcolor: 'white',
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#1e3a5f',
                  },
                },
              }}
              size="small"
            >
              <MenuItem value="">Alle Kunden</MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.company_name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Projekt filtern"
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              disabled={!filterCustomer && customers.length > 0}
              helperText={!filterCustomer && customers.length > 0 ? "Bitte zuerst Kunde auswählen" : ""}
              sx={{
                minWidth: 250,
                bgcolor: 'white',
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#1e3a5f',
                  },
                },
              }}
              size="small"
            >
              <MenuItem value="">Alle Projekte</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Tasks durchsuchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Name oder Beschreibung"
              sx={{
                flex: 1,
                bgcolor: 'white',
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#1e3a5f',
                  },
                },
              }}
              size="small"
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  sx={{
                    color: '#1e3a5f',
                    '&.Mui-checked': {
                      color: '#1e3a5f',
                    },
                  }}
                />
              }
              label="Deaktivierte Tasks anzeigen"
            />
            {(filterCustomer || filterProject || searchQuery) && (
              <Button
                variant="outlined"
                onClick={() => {
                  setFilterCustomer('');
                  setFilterProject('');
                  setSearchQuery('');
                }}
                sx={{
                  borderColor: '#e0e0e0',
                  color: '#616161',
                  '&:hover': {
                    borderColor: '#1e3a5f',
                    bgcolor: 'rgba(30, 58, 95, 0.04)',
                  },
                }}
              >
                Filter zurücksetzen
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {allProjects.length === 0 ? (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary',
              }}
            >
              <TaskIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" gutterBottom>
                Keine Projekte vorhanden
              </Typography>
              <Typography variant="body2">
                Bitte erstellen Sie zuerst ein Projekt.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : Object.keys(groupedTasks).length === 0 ? (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary',
              }}
            >
              <SearchIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" gutterBottom>
                Keine Tasks gefunden
              </Typography>
              <Typography variant="body2">
                {searchQuery
                  ? `Keine Tasks für "${searchQuery}" gefunden.`
                  : 'Keine Tasks in diesem Projekt.'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        Object.values(groupedTasks).map(({ project, tasks: projectTasks }) => (
          <Card key={project.id} sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    display: 'inline-block',
                    px: 1.5,
                    py: 0.5,
                    bgcolor: '#f5f5f5',
                    borderRadius: 0.5,
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#424242', fontSize: '0.875rem', fontWeight: 600 }}>
                    {project.name}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {projectTasks.length} {projectTasks.length === 1 ? 'Task' : 'Tasks'}
                </Typography>
              </Box>

              {projectTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
                  Keine Tasks in diesem Projekt
                </Typography>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600, width: '120px' }}>Task-ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Task Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {projectTasks.map((task) => (
                        <TableRow
                          key={task.id}
                          sx={{
                            bgcolor: !task.is_active ? 'rgba(158, 158, 158, 0.1)' : 'transparent',
                            '&:hover': {
                              bgcolor: !task.is_active ? 'rgba(158, 158, 158, 0.15)' : 'action.hover',
                            },
                          }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: task.task_id ? '#1e3a5f' : 'text.disabled',
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                              }}
                            >
                              {task.task_id || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                  color: !task.is_active ? 'text.disabled' : 'text.primary',
                                  textDecoration: !task.is_active ? 'line-through' : 'none',
                                }}
                              >
                                {task.name}
                              </Typography>
                              {!task.is_active && (
                                <Chip
                                  label="Deaktiviert"
                                  size="small"
                                  sx={{
                                    height: '20px',
                                    fontSize: '0.7rem',
                                    bgcolor: 'rgba(158, 158, 158, 0.2)',
                                    color: 'text.secondary',
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                color: !task.is_active ? 'text.disabled' : 'text.secondary',
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {task.description ? task.description.replace(/<[^>]*>/g, '').substring(0, 100) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              {!task.is_active ? (
                                <IconButton
                                  size="small"
                                  onClick={() => handleReactivateTask(task.id)}
                                  sx={{
                                    color: '#4caf50',
                                    '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.08)' },
                                  }}
                                  title="Task reaktivieren"
                                >
                                  <RestoreIcon fontSize="small" />
                                </IconButton>
                              ) : (
                                <>
                                  <IconButton
                                    size="small"
                                    onClick={() => openTimeEntryModal(task)}
                                    sx={{
                                      color: '#e01e26',
                                      '&:hover': { bgcolor: 'rgba(224, 30, 38, 0.08)' },
                                    }}
                                    title="Zeiteintrag erstellen"
                                  >
                                    <TimeIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEdit(task)}
                                    sx={{
                                      color: '#1e3a5f',
                                      '&:hover': { bgcolor: 'rgba(30, 58, 95, 0.08)' },
                                    }}
                                    title="Task bearbeiten"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {/* Create/Edit Modal - Now using TaskEditDialog */}
      {showModal && isCreating && (
        <TaskCreateDialog
          key="task-create"
          open={showModal}
          onClose={closeModal}
          projects={projects}
          customers={customers}
          allProjects={allProjects}
          onTaskCreated={handleTaskCreated}
        />
      )}

      {showModal && !isCreating && editingTask && (
        <TaskEditDialog
          key={`task-edit-${editingTask?.id}`}
          open={showModal}
          onClose={closeModal}
          task={editingTask}
          projectId={editingTask?.project_id}
          onTaskUpdated={handleTaskUpdated}
        />
      )}

      {/* Time Entry Modal */}
      <Dialog
        open={showTimeEntryModal}
        onClose={closeTimeEntryModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Zeiteintrag erstellen
          </Typography>
          <IconButton onClick={closeTimeEntryModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="time-entry-form" onSubmit={handleTimeEntrySubmit}>
            <TextField
              select
              fullWidth
              margin="normal"
              label="Projekt"
              name="project_id"
              value={timeEntryData.project_id}
              onChange={handleTimeEntryChange}
              required
              disabled
            >
              <MenuItem value="">Projekt auswählen</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              margin="normal"
              label="Task"
              name="task_id"
              value={timeEntryData.task_id}
              onChange={handleTimeEntryChange}
              required
              disabled
            >
              <MenuItem value="">Task auswählen</MenuItem>
              {tasks.filter(task => task.project_id === timeEntryData.project_id).map((task) => (
                <MenuItem key={task.id} value={task.id}>
                  {task.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              margin="normal"
              label="Subtask Kommentar"
              name="subtask_comment"
              value={timeEntryData.subtask_comment}
              onChange={handleTimeEntryChange}
              multiline
              rows={3}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Stunden"
              name="hours"
              type="number"
              value={timeEntryData.hours}
              onChange={handleTimeEntryChange}
              inputProps={{ step: 0.25, min: 0.25 }}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Datum"
              name="date"
              type="date"
              value={timeEntryData.date}
              onChange={handleTimeEntryChange}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeTimeEntryModal}
            sx={{
              color: '#1e3a5f',
              fontWeight: 600,
            }}
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            form="time-entry-form"
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
              },
            }}
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AllTasksPage;
